import { nanoid } from 'nanoid'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { useTranslation } from 'react-i18next'

import { useSaveDocumentImage } from '~/components/features/SaveDocumentImageDialog/hooks/useSaveDocumentImage'
import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery/useSurveyQueryData'
import {
  assertExists,
  createImageData,
  exists,
  FigureInfoHelpers,
  type SurveyFilterGroups,
} from '~/utils'
import { buildSurveyFilterTextGroups } from '~/utils/specifics/buildSurveyFilterTextGroups'
import { type SegmentDisplayType } from '~/utils/specifics/segmentDisplayType'

import { compile } from '../../../routes/paths'
import { type CustomLabels } from '../atoms'

import { findSelectedMatrixParents } from './buildTableElements'
import { createTaskElementsBuilder } from './buildTaskElements'
import { formatDocumentImageLabel } from './formatDocumentImageLabel'
import { kindToPathKey, planRenderTasks } from './planRenderTasks'
import { type BasicOutputsResult } from './useGenerateBasicOutputs'

const fetchDocumentImagesDocument = graphql(`
  query RenderAndSaveDocumentImages_DocumentImages(
    $brandAccountId: ID!
    $limit: Int!
    $offset: Int!
  ) {
    documentImages(
      brandAccountId: $brandAccountId
      status: active
      pagination: { limit: $limit, offset: $offset }
      filter: { showAll: true }
    ) {
      id
      imageURL
      restoreKey
    }
  }
`)

export type SavedImageEntry = {
  brandId: number | null
  brandName: string
  filterName: string
  filterPatternIndex: number
  // 自社 brand this group-wide copy is framed for; null for per-brand tables and for a
  // run with no 自社 flagged. Distinct from brandId/brandName — see RenderTask.
  framingBrandName: string | null
  id: string
  imageURL: string
  outputKey: string
  outputType: string
  restoreKey: string
  segmentMode: string
  title: string
}

// Keyed by String(filterPatternIndex) — a stable unique key. Filter pattern names
// are free-text and not unique, so keying by name would merge distinct patterns.
// Each SavedImageEntry still carries filterName for display.
export type SavedImagesMap = Map<string, SavedImageEntry[]>

type RenderAndSaveProgress = {
  completed: number
  errors: Error[]
  isComplete: boolean
  total: number
}

const initialProgress: RenderAndSaveProgress = {
  completed: 0,
  errors: [],
  isComplete: false,
  total: 0,
}

type StartContext = {
  aiCommentsEnabled: boolean
  brandAccountId: string
  brandGroupId: number
  brandIdToOriginalIndex: Map<number, number>
  customerAttributeGroups: {
    customerAttributes: { id: string; name: string }[]
    id: string
    name: string
  }[]
  customLabels: CustomLabels
  customRoleAxes: Record<string, 'customerAttribute' | 'question' | undefined>
  filterPatternFilterGroups: SurveyFilterGroups[]
  getBrandAligned: (role: string) => boolean
  isEntireBrandGroupSelected: boolean
  ownBrandIds: readonly number[]
  questionAssignments: Record<string, string[]>
  questions: {
    mt_questions: string[]
    question_id: string
    question_text: string
  }[]
  result: BasicOutputsResult
  selectedBrandIds: number[] // explicit; may be empty when isEntireBrandGroupSelected is true — forwarded to planRenderTasks
  surveyId: string
}

const segmentModeToDisplayType: Record<string, SegmentDisplayType> = {
  '5segs': 'fiveSegs',
  '9segs': 'nineSegs',
}

const buildRestoringParams = ({
  aiCommentsEnabled,
  brandAccountId,
  brandGroupId,
  extraState,
  filterGroups,
  hash,
  pathKey,
  revision,
  segmentMode,
  surveyId,
}: {
  aiCommentsEnabled: boolean
  brandAccountId: string
  brandGroupId: number
  extraState?: Record<string, unknown>
  filterGroups: SurveyFilterGroups
  hash: string
  pathKey: 'customerAnalysis' | 'customizedAnalysis' | 'standardAnalysis'
  revision: number
  segmentMode: '5segs' | '9segs'
  surveyId: string
}) => ({
  brandAccountId,
  // Attach a unique commentRef only when this run generates comments (AI on), so
  // each saved figure maps 1:1 to its DocumentImage on restore. No comments → no
  // marker, so the restore key stays content-deduped.
  ...(aiCommentsEnabled ? { commentRef: nanoid() } : {}),
  replace: {
    path: compile('restoreSurveyFilter', { surveyId }),
    state: {
      replace: {
        path: compile(pathKey, { surveyId }),
        state: {
          brandGroupId,
          dataDisplayType: 'percentage',
          fiveSegmentHiddenColumnKeys: [],
          fiveSegmentSortValue: null,
          hash,
          hiddenRowIds: [],
          nineSegmentHiddenColumnKeys: [],
          nineSegmentSortValue: null,
          revision,
          segmentDisplayType:
            segmentModeToDisplayType[segmentMode] ?? 'nineSegs',
          version: 2,
          ...extraState,
        },
      },
      surveyFilter: {
        filterGroups,
        surveyId,
      },
    },
  },
})

// Pre-render a React element to an HTMLElement with sufficient wait time
// for React 18 concurrent rendering to complete (complex tables with colSpan/rowSpan
// need more than one tick to fully render)
const renderToHtmlElement = async (
  element: React.ReactElement,
): Promise<{ cleanup: () => void; container: HTMLElement }> => {
  const container = document.createElement('div')
  container.style.position = 'absolute'
  container.style.top = '0'
  container.style.left = '-10000px'
  document.body.appendChild(container)

  const root = createRoot(container)
  root.render(element)

  // Wait for multiple animation frames to ensure concurrent rendering completes
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resolve()
      })
    })
  })

  return {
    cleanup: () => {
      root.unmount()
      document.body.removeChild(container)
    },
    container,
  }
}

export const useRenderAndSaveDocumentImages = () => {
  const saveDocumentImage = useSaveDocumentImage()
  const graphQLRequest = useGraphQLRequest()
  const survey = useSurveyQueryData()
  const { t } = useTranslation()
  const [progress, setProgress] =
    React.useState<RenderAndSaveProgress>(initialProgress)

  const start = React.useCallback(
    async (context: StartContext): Promise<SavedImagesMap> => {
      const {
        aiCommentsEnabled,
        brandAccountId,
        brandGroupId,
        brandIdToOriginalIndex,
        customerAttributeGroups,
        customLabels,
        customRoleAxes,
        filterPatternFilterGroups,
        getBrandAligned,
        isEntireBrandGroupSelected,
        ownBrandIds,
        questionAssignments,
        questions,
        result,
        selectedBrandIds,
        surveyId,
      } = context

      assertExists(survey, t('assertion_failed'))

      const brandGroup = survey.brandGroups.find((bg) => bg.id === brandGroupId)
      const brandGroupName = brandGroup?.name
      const groupBrands = (brandGroup?.brands ?? []).map((b) => ({
        id: b.id,
        name: b.name,
        number: b.number,
      }))

      // PLANNING is engine-free: the count/identity of tables comes from wizard
      // input alone (see planRenderTasks). The engine result only fills content.
      const tasks = planRenderTasks({
        aiCommentsEnabled,
        brandIdToOriginalIndex,
        customerAttributeGroups,
        customRoleAxes,
        filterPatternDrafts: filterPatternFilterGroups.map((_, index) => ({
          name: result.filterResults[index]?.filterName ?? '',
        })),
        getBrandAligned,
        groupBrands,
        isEntireBrandGroupSelected,
        ownBrandIds,
        questionAssignments,
        questions,
        selectedBrandIds,
      })

      const surveyFilterByFilterIndex = new Map<
        number,
        ReturnType<typeof buildSurveyFilterTextGroups>
      >()
      for (const [filterIndex] of filterPatternFilterGroups.entries()) {
        const filterGroups = filterPatternFilterGroups[filterIndex] ?? []
        surveyFilterByFilterIndex.set(
          filterIndex,
          buildSurveyFilterTextGroups({
            brandGroups: survey.brandGroups,
            customerAttributeGroups: survey.customerAttributeGroups,
            filterGroups,
            questions: survey.questions,
            t,
          }),
        )
      }

      const buildTaskElements = createTaskElementsBuilder({
        customerAttributeGroups,
        questions,
        result,
        // Same source + filter as planRenderTasks `targetBrands` and the analyzer
        // resolver's group-filter brand list → resolver-matching index order.
        selectedBrandsInOrder: groupBrands.filter((b) =>
          selectedBrandIds.includes(b.id),
        ),
        surveyQuestions: survey.questions,
        t,
      })

      setProgress({
        completed: 0,
        errors: [],
        isComplete: false,
        total: tasks.length,
      })

      const errors: Error[] = []
      const savedImages: {
        brandId: number | null
        brandName: string
        filterName: string
        filterPatternIndex: number
        framingBrandName: string | null
        id: string
        outputKey: string
        outputType: string
        segmentMode: string
        title: string
      }[] = []

      for (const task of tasks) {
        // Reconstruct the engine outputType from the wizard-derived kind/role for
        // display + persistence (the descriptor is engine-free).
        const outputType =
          task.kind === 'customer'
            ? 'customerAnalysis'
            : `${task.kind === 'standard' ? 'standardAnalysis' : 'customizedAnalysis'}_${task.role}`
        const title = formatDocumentImageLabel({
          task,
          brandGroupName,
          surveyTitle: survey.title,
          customLabels,
          t,
        })

        // Build is engine-data-dependent and the plan fixes the output count, so a
        // missing output / builder error is a real data bug — FAIL FAST (propagate
        // out of start). Keep it OUTSIDE the per-task try so it is never swallowed
        // into the recoverable render/save error recovery below.
        const tableElements = buildTaskElements(task)
        const tableElement = tableElements.find(
          (el) => el.segmentMode === task.segmentMode,
        )
        if (!exists(tableElement)) {
          throw new Error(
            `basic-output: no ${task.segmentMode} table for kind=${task.kind}, role=${task.role}, brandId=${task.brandId}`,
          )
        }

        try {
          const filterGroups = filterPatternFilterGroups[task.filterIndex] ?? []

          // #4 — guarantee cleanup even if createImageData throws.
          const { cleanup, container } = await renderToHtmlElement(
            tableElement.element,
          )
          let imageData: string | null
          try {
            ;[imageData] = await createImageData(container, 'chart')
          } finally {
            cleanup()
          }

          const pathKey = kindToPathKey[task.kind]

          const isCustomerAnalysis = task.kind === 'customer'
          const isCustomizedAnalysis = task.kind === 'customized'

          // Resolve questionIds for customer/standard tasks (already prefix-stripped
          // during planning, with per-brand sub-question IDs applied for matrix
          // questions). The customer-attribute axis carries a GROUP id, not
          // questionIds, so it is forced to [] here.
          const isCustomerAttributeAxis = task.axis === 'customerAttribute'
          const questionIdsForRole = isCustomerAttributeAxis
            ? []
            : (task.allowedQuestionIds ?? [])
          const customerAttributeIdsForRole = isCustomerAttributeAxis
            ? (task.customerAttributeIds ?? [])
            : []
          // customized only: the raw wizard selection, already validated by
          // buildTaskElements above. Reused (not re-derived from
          // questionAssignments) so figureInfo/restoringParams/matrix-parent
          // detection below cannot drift from what actually built the table.
          const customizedSelectedQuestionIds = task.selectedQuestionIds ?? []

          // Customized figures restore against GroupCountsFigures, which
          // partitions questions by `mt_questions.length` (see useGroupedQuestions)
          // and renders a matrix block keyed `matrix-${question_id}-all` for matrix
          // questions, but a single SequentialTable keyed `sequential` otherwise.
          // The hash must equal the read-key of the table that actually mounts,
          // or the server-injected comment lands in an unread slot and is lost.
          // Share findSelectedMatrixParents with the table builder so the rendered
          // image, the figureType and this hash cannot disagree about which table
          // this figure is. Only customized tasks have a matrix table.
          const customizedMatrixQuestionId = isCustomizedAnalysis
            ? findSelectedMatrixParents({
                questions: survey.questions,
                selectedQuestionIds: customizedSelectedQuestionIds,
              })[0]?.question_id
            : undefined

          // #2(A) — figureType per kind, matching each existing analysis page:
          //   customer   → the segment view ('5segs' / '9segs')
          //   standard   → OMIT (StandardAnalysisPage sets no figureType)
          //   customized → matrix vs individual (CustomizedAnalysisPage values)
          const figureInfo = isCustomizedAnalysis
            ? FigureInfoHelpers.buildFigureInfo({
                brandAccountId,
                brandGroupName,
                customerAttributeGroups: survey.customerAttributeGroups,
                customerAttributeIds: [],
                dataDisplayType: 'percentage',
                // Non-matrix customized tables render via the same SequentialTable
                // as CustomizedAnalysisPage, which uses
                // useFigureInfo('continuous_questionnaire'). Match that so a
                // reopened image lands on the right widget (matrix → group_matrix).
                figureType: exists(customizedMatrixQuestionId)
                  ? 'group_matrix'
                  : 'continuous_questionnaire',
                groupFilters: [],
                pathKey,
                questionIds:
                  0 < customizedSelectedQuestionIds.length
                    ? customizedSelectedQuestionIds
                    : undefined,
                questions: survey.questions,
                resultViewerType: 'sequential',
                surveyFilter: surveyFilterByFilterIndex.get(task.filterIndex),
                surveyTitle: survey.title,
                t,
              })
            : FigureInfoHelpers.buildFigureInfo({
                brandAccountId,
                brandGroupName,
                brandName: task.brandName,
                ...(!isCustomerAnalysis
                  ? {
                      customerAttributeGroups: survey.customerAttributeGroups,
                      customerAttributeIds: customerAttributeIdsForRole,
                    }
                  : {}),
                dataDisplayType: 'percentage',
                ...(isCustomerAnalysis ? { figureType: task.segmentMode } : {}),
                pathKey,
                questionIds:
                  0 < questionIdsForRole.length
                    ? questionIdsForRole
                    : undefined,
                questions: survey.questions,
                segmentDisplayType: segmentModeToDisplayType[task.segmentMode],
                surveyFilter: surveyFilterByFilterIndex.get(task.filterIndex),
                surveyTitle: survey.title,
                t,
              })

          const isStandardAnalysis =
            !isCustomerAnalysis && !isCustomizedAnalysis
          if (isStandardAnalysis && !exists(task.brandId)) {
            throw new Error(
              'basic-output: standard restoringParams missing brandId',
            )
          }

          const restoringParams = buildRestoringParams({
            aiCommentsEnabled,
            brandAccountId,
            brandGroupId,
            extraState: isCustomerAnalysis
              ? undefined
              : isCustomizedAnalysis
                ? {
                    customerAttributeIds: [],
                    groupInfos: [],
                    groupNameMap: {},
                    individualSortValue: null,
                    matrixSortValue: null,
                    questionIds: customizedSelectedQuestionIds,
                    resultViewerType: 'sequential',
                    sequentialSortValue: null,
                  }
                : {
                    brandIds: exists(task.brandId) ? [task.brandId] : [],
                    customerAttributeIds: customerAttributeIdsForRole,
                    questionIds: questionIdsForRole,
                  },
            filterGroups,
            hash: isCustomerAnalysis
              ? task.segmentMode
              : isCustomizedAnalysis
                ? exists(customizedMatrixQuestionId)
                  ? `matrix-${customizedMatrixQuestionId}-all`
                  : 'sequential'
                : String(task.brandId),
            pathKey: isCustomerAnalysis
              ? 'customerAnalysis'
              : isCustomizedAnalysis
                ? 'customizedAnalysis'
                : 'standardAnalysis',
            revision: isCustomizedAnalysis ? 6 : 2,
            segmentMode: task.segmentMode,
            surveyId,
          })

          const imageId = await saveDocumentImage({
            figureInfo,
            imageData,
            restoringParams,
            title,
          })

          // A null imageId means the save FAILED. Throw so the surrounding
          // per-task try/catch records it in `errors` (surfaced, not silent)
          // and continues — never let a failed save drop the count silently.
          if (!exists(imageId)) {
            throw new Error(
              `basic-output: saveDocumentImage returned null for ${title}`,
            )
          }
          const brandId: number | null = exists(task.brandId)
            ? task.brandId
            : null
          savedImages.push({
            brandId,
            brandName: task.brandName ?? 'all',
            filterName: task.filterName,
            framingBrandName: task.framingBrandName ?? null,
            filterPatternIndex: task.filterIndex,
            id: imageId,
            outputKey: task.outputKey,
            outputType,
            segmentMode: task.segmentMode,
            title,
          })
        } catch (error) {
          console.error(
            '[BOG] render/save failed for:',
            outputType,
            task.filterName,
            error,
          )
          errors.push(error instanceof Error ? error : new Error(String(error)))
        }

        setProgress((prev) => ({
          ...prev,
          completed: prev.completed + 1,
          errors: [...errors],
        }))
      }

      setProgress((prev) => ({ ...prev, isComplete: true }))

      // Fetch imageURLs for saved images
      const savedImagesMap: SavedImagesMap = new Map()

      if (0 < savedImages.length) {
        // Page by offset until every saved id is resolved. A fixed top-N window
        // can miss this batch's older ids when other writers add active images
        // during the upload window, so keep paging while ids remain wanted.
        const wanted = new Set(savedImages.map((s) => s.id))
        const imageURLById = new Map<string, string>()
        const restoreKeyById = new Map<string, string>()
        const pageLimit = savedImages.length + 50
        for (let offset = 0; 0 < wanted.size; offset += pageLimit) {
          const page = await graphQLRequest({
            document: fetchDocumentImagesDocument,
            variables: { brandAccountId, limit: pageLimit, offset },
          })
          const rows = page.documentImages ?? []
          for (const di of rows) {
            if (wanted.has(di.id)) {
              imageURLById.set(di.id, di.imageURL)
              restoreKeyById.set(di.id, di.restoreKey)
              wanted.delete(di.id)
            }
          }
          if (rows.length < pageLimit) {
            break
          }
        }

        for (const saved of savedImages) {
          // A just-saved image must be fetchable; its absence is an invariant
          // violation. This is post-loop (no per-task catch), so FAIL FAST.
          const imageURL = imageURLById.get(saved.id)
          const restoreKey = restoreKeyById.get(saved.id)
          if (!exists(imageURL) || !exists(restoreKey)) {
            throw new Error(
              `basic-output: saved DocumentImage id=${saved.id} has no imageURL/restoreKey in the fetch result`,
            )
          }

          if ((saved.brandName === 'all') !== (saved.brandId === null)) {
            throw new Error(
              `SavedImageEntry invariant violated: brandName='${saved.brandName}' but brandId=${saved.brandId}`,
            )
          }
          // #3 — key by filterPatternIndex (stable, unique), NOT filterName.
          const mapKey = String(saved.filterPatternIndex)
          const entries = savedImagesMap.get(mapKey) ?? []
          entries.push({
            brandId: saved.brandId,
            brandName: saved.brandName,
            filterName: saved.filterName,
            filterPatternIndex: saved.filterPatternIndex,
            framingBrandName: saved.framingBrandName,
            id: saved.id,
            imageURL,
            outputKey: saved.outputKey,
            outputType: saved.outputType,
            restoreKey,
            segmentMode: saved.segmentMode,
            title: saved.title,
          })
          savedImagesMap.set(mapKey, entries)
        }
      }

      return savedImagesMap
    },
    [graphQLRequest, saveDocumentImage, survey, t],
  )

  return { progress, start }
}
